import styles from "./customHeader.module.scss";
import React, { useRef, useEffect } from "react";
import Icon from "../../icons/Icon";
import { ButtonBox } from "../../button/Button";
import Badge from "../../tag/Badge";
import { ImgNew } from "../../icons/18";
import {
  Arrowback as HeaderIconBack,
  ArrowbackWhite as HeaderIconBackWhite,
  X as HeaderIconClose,
  XWhite as HeaderIconCloseWhite,
  Home as HeaderIconHome,
  HomeWhite as HeaderIconHomeWhite,
} from "../../icons/navi";

interface CustomHeaderProps {
  className?: string;
  title?: string | React.ReactNode;
  titleColor?: string;
  titleStyle?: React.CSSProperties;
  iconColor?: string;
  backgroundColor?: string;
  style?: React.CSSProperties;
  goHome?: () => void;
  goBack?: () => void;
  close?: () => void;
  actionItems?: {
    name: string;
    isIcon?: boolean;
    badge?: string | number | boolean;
    badgeType?: "text" | "icon" | "dot";
    callback?: () => void;
  }[];
  scrollHide?: boolean;
  headerPostion?: "fixed" | "absolute" | "relative";
  linkItem?: { name: string; callback: () => void; color?: string };
  children?: React.ReactNode;
  id?: string;
}

// 액션 아이템 타입
type TActionItem =
  | "search"
  | "chat"
  | "push"
  | "like"
  | "close"
  | "check"
  | "back"
  | "stop"
  | "more";
interface IHeaderIconType {
  name: TActionItem | "registry" | "shopping" | "info" | "share" | "likeFill" | "edit" | "budget";
  componentName: string; // 특별한 경우가 아니라면 name과 동일 + 앞글자만 대문자
}
export const HEADER_ICON_TYPE: IHeaderIconType[] = [
  { name: "search", componentName: "Search" },
  { name: "push", componentName: "Alarm" },
  { name: "like", componentName: "HeartOutline" },
  { name: "close", componentName: "X" },
  { name: "back", componentName: "Arrowback" },
  { name: "stop", componentName: "X" },
  { name: "more", componentName: "More" },

  // customHeader에 있음
  { name: "registry", componentName: "Registry" },
  { name: "shopping", componentName: "ShoppingCart" },

  { name: "info", componentName: "Info" },
  { name: "share", componentName: "Share" },
  { name: "likeFill", componentName: "HeartFill" },
  { name: "edit", componentName: "Edit" },

  //예산관리 헤더
  { name: "budget", componentName: "Budget" },
];

// 스크롤 숨김 훅
export const useScrollHide = ({
  headerRef,
  ableCondition = true,
}: {
  headerRef: React.RefObject<HTMLDivElement>;
  ableCondition?: boolean;
}) => {
  const lastScrollTop = useRef(0);
  const lastType = useRef("down");
  const dispatchTypePosition = useRef(0);
  const postionBeforeDispatch = useRef(0);

  useEffect(() => {
    const wrap = headerRef.current;
    if (!wrap || !ableCondition) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > lastScrollTop.current) {
        // Scroll down
        if (lastType.current === "up") {
          const matrix = new WebKitCSSMatrix(window.getComputedStyle(wrap).transform);
          dispatchTypePosition.current = scrollTop;
          postionBeforeDispatch.current = matrix.m42;
        }
        let v = -postionBeforeDispatch.current + (scrollTop - dispatchTypePosition.current);
        if (v > 56) v = 56;
        wrap.style.transform = `translateY(${-v}px)`;
        lastType.current = "down";
      } else {
        // Scroll up
        if (lastType.current === "down") {
          const matrix = new WebKitCSSMatrix(window.getComputedStyle(wrap).transform);
          dispatchTypePosition.current = scrollTop;
          postionBeforeDispatch.current = matrix.m42;
        }
        let v = -postionBeforeDispatch.current - (dispatchTypePosition.current - scrollTop);
        if (v <= 0) v = 1;
        wrap.style.transform = `translateY(${-(v - 1)}px)`;
        lastType.current = "up";
      }

      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [headerRef, ableCondition]);

  return null;
};

/**
 * AppHeader와 동일한 스타일 컴포넌트 / 뒤로가기, 닫기 함수를 받아서 사용할 수 있도록 추가
 * AppHeader 구조/클래스 등 변경 시 동일하게 변경 필요 (Appheader와 다를 경우 맞춰주시면 됩니다!)
 * titleColor, iconColor, backgroundColor, style 등 커스텀 가능 (단, 색깔별 icon 이미지 같은 경우엔 추가 업로드가 필요할 수 있음 className=icon-close-${iconColor} 이런식으로 처리됨)
 *
 * @param className 커스텀 클래스
 * @param title 헤더 타이틀
 * @param titleColor 헤더 타이틀 색상
 * @param titleStyle 헤더 타이틀 스타일
 * @param iconColor 아이콘 색상
 * @param backgroundColor 헤더 배경색
 * @param style 커스텀 스타일
 * @param goBack 뒤로가기 함수
 * @param close 닫기 함수
 * @param actionItems 헤더 우측 액션 아이템
 * @param scrollHide 스크롤 시 헤더 숨김 여부
 * @param headerPostion 헤더 포지션
 */
const CustomHeader = ({
  className = "",
  title,
  titleColor = "#000",
  titleStyle = {},
  iconColor = "black",
  backgroundColor = "#fff",
  style = {},
  goBack,
  goHome,
  close,
  actionItems,
  scrollHide = true,
  headerPostion = "fixed",
  linkItem,
  children,
  id,
}: CustomHeaderProps) => {
  // 스크롤 방향에 따라 숨김 처리 이벤트 -> 앱헤더 상단에 훅으로 빼서 처리했습니다. 커스텀에서는 해당 훅 가져와서 사용
  const headerRef = useRef<HTMLDivElement>(null);
  useScrollHide({
    headerRef: headerRef,
    ableCondition: scrollHide,
  });

  return (
    <>
      <div
        id={id ? id : ""}
        className={`${styles["app-header-wrap"]} ${styles["app-header-custom"]} ${className} ${!scrollHide ? styles["app-header-not-scrollhide"] : ""}`}
        style={{
          backgroundColor: backgroundColor,
          position: headerPostion,
          ...style,
        }}
        ref={headerRef}
      >
        {
          <>
            <div className={styles["app-header-inner"]}>
              <div className={`${styles["header-left"]}`}>
                {goBack && (
                  <ButtonBox
                    type="icon"
                    onTouchEnd={(e: React.TouchEvent) => {
                      e.stopPropagation();
                      e.preventDefault();
                      goBack();
                    }}
                    noPaddingPosition="all"
                  >
                    {iconColor === "white" ? <HeaderIconBackWhite /> : <HeaderIconBack />}
                  </ButtonBox>
                )}
                {goHome && (
                  <ButtonBox
                    type="icon"
                    onTouchEnd={(e: React.TouchEvent) => {
                      e.stopPropagation();
                      e.preventDefault();
                      goHome();
                    }}
                    noPaddingPosition="all"
                  >
                    {iconColor === "white" ? <HeaderIconHomeWhite /> : <HeaderIconHome />}
                  </ButtonBox>
                )}
                {title && (
                  <h2
                    className={styles["header-title"]}
                    style={{ color: titleColor, ...titleStyle }}
                  >
                    {title}
                  </h2>
                )}
              </div>
              <div
                className={styles["header-action-item-wrap"]}
                style={{
                  marginRight:
                    (close || (!close && actionItems && actionItems.length > 0)) && !linkItem
                      ? "-10px"
                      : "",
                }} // 닫기버튼 || actionItems가 끝으로 올때 조정 아이콘 여백 때문에 우측 여백 축소용
              >
                {/* 액션아이템 배열이 있으면 보여줌 */}
                {actionItems &&
                  actionItems.length > 0 &&
                  actionItems.map((item) => (
                    <div
                      style={{ display: "inline-flex" }}
                      key={`app-header-actions-${item.name}`}
                      className={`${styles[item.name]}`}
                    >
                      <ButtonBox
                        type="icon"
                        autoHeight
                        noPaddingPosition="all"
                        transparent="all"
                        style={{ padding: 0 }}
                        onTouchEnd={(e: React.TouchEvent) => {
                          e.stopPropagation();
                          e.preventDefault();
                          item.callback && item.callback();
                        }}
                      >
                        {item.isIcon ? (
                          <>
                            {HEADER_ICON_TYPE.filter((icon) => icon.name === item.name)[0] ? (
                              <Icon
                                folderName="navi"
                                name={
                                  HEADER_ICON_TYPE.filter((icon) => icon.name === item.name)[0]
                                    .componentName + (iconColor === "white" ? "White" : "")
                                }
                                size={40}
                              />
                            ) : (
                              // HEADER_ICON_TYPE에 없는 경우라면 기존대로 나오게
                              <i
                                className={`${styles.icon} ${styles[`icon-${item.name}-${iconColor}`]}`}
                              ></i>
                            )}
                            {item?.badge ? (
                              item.badgeType === "dot" ? (
                                <Badge
                                  type="dot"
                                  marginForCustom={item?.isIcon ? "6px 8px 0 0" : ""}
                                />
                              ) : item?.badgeType === "text" && typeof item.badge !== "boolean" ? (
                                <Badge
                                  text={item.badge.toString()}
                                  type="text"
                                  marginForCustom={
                                    item?.isIcon
                                      ? `0 ${item?.badge.toString().length > 1 ? "19px" : "18px"} 0 0`
                                      : ""
                                  }
                                />
                              ) : (
                                <Badge
                                  type="text"
                                  marginForCustom={item?.isIcon ? "0 18px 0 0" : ""}
                                  backgroundColor="transparent"
                                >
                                  <Icon IconComponent={<ImgNew />} size={18} />
                                </Badge>
                              )
                            ) : null}
                          </>
                        ) : (
                          item.name
                        )}
                      </ButtonBox>
                    </div>
                  ))}
                {close && (
                  <div style={{ display: "inline-flex" }} key={`app-header-actions-close`}>
                    <ButtonBox
                      type="icon"
                      style={{ padding: 0 }}
                      onTouchEnd={(e: React.TouchEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                        close();
                      }}
                    >
                      {iconColor === "white" ? <HeaderIconCloseWhite /> : <HeaderIconClose />}
                    </ButtonBox>
                  </div>
                )}
              </div>
              {/* 링크아이템 있는 경우 -> 액션아이템은 아이콘형태 & 다른 컴포넌트에서 재활용 가능한 형태인 경우에만 사용했고 단일 텍스트 링크 연결&특정 페이지에서만 사용(ex 소셜로그인 상단 둘러보기)일 때는 다르게 사용하려고 추가한 항목입니다. */}
              {linkItem && (
                <div
                  className={styles["app-header-link"]}
                  style={{ color: linkItem?.color ? linkItem.color : "" }}
                >
                  <button onClick={linkItem.callback}>{linkItem.name}</button>
                </div>
              )}
            </div>
            {children && children}
          </>
        }
      </div>
      {/* 헤더 공간만큼 여백이 필요한 경우에만 노출 / 높이는 헤더 높이로 맞춰야함 */}
      {headerPostion !== "relative" && (
        <div
          className={`${styles["app-header-blank"]} ${id === "AppHeaderWrap" ? "app-header-blank" : ""}`}
          style={{ height: 56 }}
        />
      )}
    </>
  );
};

export default CustomHeader;
